import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, Receipt, TrendingUp, FileText } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { fetchOrders, fetchProducts, fetchCatalogStatus, uploadUrl } from "@/lib/api";
import { fmtCAD } from "@/lib/currency";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user, isApproved, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogs, setCatalogs] = useState({});

  useEffect(() => {
    if (authLoading) return;
    (async () => {
      try {
        const [o, p, c] = await Promise.all([
          fetchOrders().catch(() => []),
          fetchProducts({ featured: true }),
          fetchCatalogStatus().catch(() => ({})),
        ]);
        setOrders(o);
        setProducts(p);
        setCatalogs(c || {});
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading]);

  const stats = useMemo(() => {
    const total = orders.reduce((s, o) => s + Number(o.subtotal || 0), 0);
    const open = orders.filter((o) => o.status === "pending" || o.status === "confirmed" || o.status === "invoiced");
    return {
      total,
      orderCount: orders.length,
      openBalance: open.reduce((s, o) => s + Number(o.subtotal || 0), 0),
    };
  }, [orders]);

  const recent = orders.slice(0, 5);

  return (
    <div className="page-card" data-testid="dashboard-page">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Wholesale portal</p>
        <h1 className="text-2xl md:text-3xl font-semibold mt-0.5 mb-1">
          {isApproved ? "Welcome back" : "Welcome"}{user?.contact_name ? `, ${user.contact_name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          {isApproved
            ? "Your buyer account is active. Build orders from the products page or pick up where you left off."
            : "Your application is pending review. Browse products while you wait — wholesale pricing and ordering will unlock on approval."}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
          <StatCard icon={Receipt} label="Total ordered" value={fmtCAD(stats.total)} sub={`${stats.orderCount} order${stats.orderCount === 1 ? "" : "s"} all-time`} loading={loading} />
          <StatCard icon={Package} label="Open balance" value={fmtCAD(stats.openBalance)} sub="Pending + invoiced" loading={loading} />
          <StatCard icon={TrendingUp} label="Status" value={isApproved ? "Approved" : "Pending"} sub={isApproved ? "Wholesale pricing active" : "Awaiting review"} loading={false} />
        </div>

        {isApproved && catalogs.prebook?.url && (
          <a
            href={uploadUrl(catalogs.prebook.url)}
            target="_blank"
            rel="noreferrer"
            data-testid="dashboard-catalog-banner"
            className="group block mb-10 rounded-md border border-primary/40 bg-primary/[0.04] hover:bg-primary/[0.08] transition-colors"
          >
            <div className="flex items-center gap-4 md:gap-6 px-5 md:px-8 py-6">
              <div className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <FileText size={26} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.15em] text-primary mb-1">Drop 1 lookbook</p>
                <h3 className="font-serif text-xl md:text-2xl leading-tight">Download the full catalogue</h3>
                <p className="text-sm text-muted-foreground mt-1">Every style, size, and wholesale price in one PDF.</p>
              </div>
              <span className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-primary shrink-0 group-hover:gap-2 transition-all">
                Open PDF <ArrowRight size={13} strokeWidth={1.75} />
              </span>
            </div>
          </a>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <section className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold">Recent orders</h2>
              <Link to="/orders" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="dashboard-see-all-orders">
                See all <ArrowRight size={12} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="border border-border rounded-md overflow-hidden">
              {loading ? (
                <div className="p-4 space-y-2">
                  {[1,2,3].map((i) => <div key={i} className="h-8 rounded bg-secondary animate-pulse" />)}
                </div>
              ) : recent.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No orders yet. <Link to="/shop" className="text-primary underline">Browse products</Link>.
                </div>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {recent.map((o) => (
                    <li key={o.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-muted-foreground">#{o.id.slice(0,8).toUpperCase()}</span>
                        <span className="text-[11px] uppercase tracking-[0.12em] px-2 py-0.5 rounded border border-border bg-secondary">{o.status}</span>
                      </div>
                      <span className="tabular-nums">{fmtCAD(o.subtotal)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="lg:col-span-1">
            <h2 className="text-base font-semibold mb-3">Quick actions</h2>
            <div className="space-y-2">
              <ActionLink to="/shop?type=in_stock" label="Browse current stock" sub="Ships immediately" />
              {isApproved && (
                <ActionLink to="/shop?type=prebook" label="Browse prebooks" sub="Shop all upcoming releases" />
              )}
              <ActionLink to="/orders" label="Order history" sub="Invoices &amp; status" />
            </div>
          </section>
        </div>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold">Featured styles</h2>
            <Link to="/shop" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              All products <ArrowRight size={12} strokeWidth={1.5} />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1,2,3,4].map((i) => <div key={i} className="aspect-[4/5] rounded-md bg-secondary animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {products.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, loading }) {
  return (
    <div className="border border-border rounded-md p-4 bg-white">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon size={14} strokeWidth={1.5} />
        <p className="text-[11px] uppercase tracking-[0.12em]">{label}</p>
      </div>
      {loading ? (
        <div className="h-7 w-24 bg-secondary rounded animate-pulse" />
      ) : (
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
      )}
      <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>
    </div>
  );
}

function ActionLink({ to, label, sub, external }) {
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className="block px-4 py-3 border border-border rounded-md hover:border-foreground/30 transition-colors group"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{label}</span>
          <ArrowRight size={14} strokeWidth={1.5} className="text-muted-foreground group-hover:text-foreground transition-colors" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>
      </a>
    );
  }
  return (
    <Link to={to} className="block px-4 py-3 border border-border rounded-md hover:border-foreground/30 transition-colors group">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <ArrowRight size={14} strokeWidth={1.5} className="text-muted-foreground group-hover:text-foreground transition-colors" />
      </div>
      <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>
    </Link>
  );
}
